import { useEffect, useRef, useCallback } from 'react';

const useTypingAnimation = (elementRef, texts, options = {}) => {
  const timeoutRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const animationStateRef = useRef({
    textIndex: 0,
    charIndex: 0,
    isDeleting: false
  });

    const settings = {
      typingSpeed: options.typingSpeed || 100,
      deletingSpeed: options.deletingSpeed || 50,
      pauseTime: options.pauseTime || 2000,
      startDelay: options.startDelay || 1000
    };

  const clearAnimation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isAnimatingRef.current = false;
  }, []);

  const type = useCallback(() => {
    if (!elementRef.current || !texts.length || !isAnimatingRef.current) return;

    const element = elementRef.current;
    const state = animationStateRef.current;
    const currentText = texts[state.textIndex];
      
    // Safety check for valid text index
    if (!currentText) {
      state.textIndex = 0;
      return;
    }
    
    if (state.isDeleting) {
      element.textContent = currentText.substring(0, state.charIndex - 1);
      state.charIndex--;
        
      if (state.charIndex === 0) {
        state.isDeleting = false;
        state.textIndex = (state.textIndex + 1) % texts.length;
        if (isAnimatingRef.current) {
          timeoutRef.current = setTimeout(type, settings.typingSpeed);
        }
      } else {
        if (isAnimatingRef.current) {
          timeoutRef.current = setTimeout(type, settings.deletingSpeed);
        }
      }
    } else {
      element.textContent = currentText.substring(0, state.charIndex + 1);
      state.charIndex++;
        
      if (state.charIndex === currentText.length) {
        state.isDeleting = true;
        if (isAnimatingRef.current) {
          timeoutRef.current = setTimeout(type, settings.pauseTime);
        }
        } else {
        if (isAnimatingRef.current) {
          timeoutRef.current = setTimeout(type, settings.typingSpeed);
        }
      }
    }
  }, [elementRef, texts, settings.typingSpeed, settings.deletingSpeed, settings.pauseTime]);

  useEffect(() => {
    // Clear any existing animation
    clearAnimation();
    
    if (!elementRef.current || !texts.length) return;

    // Reset animation state
    animationStateRef.current = {
      textIndex: 0,
      charIndex: 0,
      isDeleting: false
    };
    
    // Clear the element
    elementRef.current.textContent = '';
    
    // Start new animation
    isAnimatingRef.current = true;
    timeoutRef.current = setTimeout(type, settings.startDelay);
    
    return clearAnimation;
  }, [elementRef, texts, type, clearAnimation, settings.startDelay]);
};

export default useTypingAnimation;